/* eslint-disable @typescript-eslint/no-unused-vars */
import { AuthContext } from "./AuthProvider";
import { Config } from "../environment/config";
import { EnvironmentContext } from "./EnvironmentProvider";
import { ExecutorUtils } from "@emed-labs/platform-utility-common-utils";
import { HttpClientError } from "@emed-labs/platform-utility-generic-clients";
import { Language } from "@emed-labs/ftf-datatypes/lib/Misc/Language";
import { LanguageContext, LanguageUtils } from "./LanguageProvider";
import { ModalContext } from "./ModalProvider";
import { PropsWithChildren, createContext, useContext, useEffect, useRef } from "react";
import { SessionDataContext } from "./SessionDataProvider";
import { useLogger } from "./LogProvider";
import { useRouter } from "next/router";
import { useStateRef } from "../hooks/UseStateRef";
import { VLabAPIContext } from "./VLabAPIProvider";
import {
	Procedure,
	SessionMetadata,
	TrainingSessionStartRequest,
	TrainingSessionStartResponse
} from "@emed-labs/virtual-lab-models";
import { ProcedureScriptUtils } from "../utils/ProcedureScriptUtils";

export interface SessionStateContext {
	initializing: boolean;
	initialize: (role: VLabRole) => Promise<any>;
	initializeError?: Error;
	sessionID?: string;
	procedureID?: string;
	procedure?: Procedure;
	procedureLocation?: `${string}/proctor` | `${string}/user`;
	sessionMetadata?: SessionMetadata | null;
	heartbeatError?: Error;
	sessionValid: boolean;
}

// eslint-disable-next-line no-shadow
export enum VLabRole {
	PATIENT = "patient",
	PROCTOR = "proctor"
}

export const SessionStateContext = createContext<SessionStateContext>({} as SessionStateContext);
SessionStateContext.displayName = "SessionStateContext";

const SESSION_HEARTBEAT_INTERVAL = 90000;

export const SessionStateProvider: React.FunctionComponent<PropsWithChildren> = (props) => {
	const AuthCtx = useContext(AuthContext);
	const VlabAPICtx = useContext(VLabAPIContext);
	const SessionData = useContext(SessionDataContext);
	const EnvCtx = useContext(EnvironmentContext);
	const Modal = useContext(ModalContext);
	const LanguageCtx = useContext(LanguageContext);
	const Router = useRouter();
	const logger = useLogger("providers.SessionStateProvider");

	const [initializing, setInitializing, initializingRef] = useStateRef(false);
	//const [patient, setPatient, patientRef] = useStateRef<Patient>();
	const [procedureID, setProcedureID, procedureIDRef] = useStateRef<string>();
	const [procedure, setProcedure, procedureRef] = useStateRef<Procedure>();
	const [procedureLocation, setProcedureLocation, procedureLocationRef] = useStateRef<
		`${string}/proctor` | `${string}/user`
	>();

	const [sessionID, setSessionID, sessionIDRef] = useStateRef<string>();
	const [sessionMetadata, setSessionMetadata] = useStateRef<SessionMetadata | null>(null);

	const [initializeError, setInitializeError, initializeErrorRef] = useStateRef<Error>();
	const [heartbeatError, setHeartbeatError] = useStateRef<Error>();
	const [sessionValid, setSessionValid] = useStateRef<boolean>(true);

	// Capture initial query params on first mount and store them
	const initialQueryParamsRef = useRef<Record<string, string> | null>(null);

	/**
	 * Capture query params IMMEDIATELY on mount, before any navigation
	 */
	useEffect(() => {
		if (!initialQueryParamsRef.current && typeof window !== "undefined") {
			const params: Record<string, string> = {};

			// Capture from window.location.search (most reliable)
			if (window.location.search) {
				const urlParams = new URLSearchParams(window.location.search);
				urlParams.forEach((value, key) => {
					if (key !== "token") {
						params[key] = value;
					}
				});
			}

			initialQueryParamsRef.current = params;
		}
	}, []);

	/**
	 * Initialize a real session after token authentication
	 * Wait for router to be ready to ensure query params are available
	 */
	useEffect(() => {
		if (Router.isReady && AuthCtx.authenticated && AuthCtx.sessionID && AuthCtx.role) {
			initialize(AuthCtx.role);
		}
	}, [Router.isReady, AuthCtx.authenticated, AuthCtx.sessionID, AuthCtx.role]);

	useEffect(() => {
		if (sessionID) {
			let intervalId: NodeJS.Timeout | null = null;

			const sendHeartbeatWithErrorHandling = async () => {
				try {
					await SessionData.sendHeartbeat();
					// Clear any previous heartbeat errors on successful heartbeat
					if (heartbeatError) {
						setHeartbeatError(undefined);
					}
					if (!sessionValid) {
						setSessionValid(true);
					}
				} catch (e) {
					// Check if it's the specific "INVALID_MEETING_ID" error - meeting has ended
					if (e.response && e.response.status === 400 && e.response.data?.code === "INVALID_MEETING_ID") {
						logger.info("Heartbeat failed", "Meeting has ended, cleaning up session", {
							code: e.response.data.code,
							message: e.response.data.message
						});

						// Mark session as invalid
						setSessionValid(false);

						// Clear the interval to stop further heartbeats
						if (intervalId) {
							clearInterval(intervalId);
							intervalId = null;
						}

						// No error message needed - this is expected when meeting ends
						// Just clean up silently
						return;
					}

					// Handle other 4xx errors (session expired, unauthorized, etc.)
					if (e.response && e.response.status >= 400 && e.response.status < 500) {
						logger.error("Heartbeat failed", "4xx error received, stopping heartbeat and terminating session", e, {
							status: e.response.status,
							statusText: e.response.statusText
						});

						// Mark session as invalid
						setSessionValid(false);

						// Clear the interval to stop further heartbeats
						if (intervalId) {
							clearInterval(intervalId);
							intervalId = null;
						}

						// Show error message to user
						await Modal.alert("Your session may no longer be valid. Please refresh the page to continue.");

						return;
					}

					// For other errors (network, 5xx, etc.), just log and continue
					logger.error("Heartbeat failed", `Heartbeat error: ${e.message}`, e);
					setHeartbeatError(e);
				}
			};

			// Set up interval for subsequent heartbeats
			intervalId = setInterval(sendHeartbeatWithErrorHandling, SESSION_HEARTBEAT_INTERVAL);

			return () => {
				if (intervalId) {
					clearInterval(intervalId);
				}
			};
		}
	}, [sessionID]);

	/**
	 * Check for and then intialize and test sessions
	 */
	useEffect(() => {
		const pid = Router.query?.pid as string;
		const isSalesDemoProcedure = pid && pid.endsWith("sales");

		if (
			Router.query?.sid &&
			pid &&
			(isSalesDemoProcedure || EnvCtx.get(Config.FLAG_ENABLE_TRAINING_ENVIRONMENT, { optional: true })) &&
			Router.query.sid.indexOf("test_session") !== -1 &&
			typeof pid === "string"
		) {
			initializeTestSession(pid);
		}
	}, [Router.query.sid, Router.query.pid]);

	const loadProcedure = async (role: VLabRole) => {
		try {
			const procedurePath = await ProcedureScriptUtils.resolveScriptPath(procedureIDRef.current);

			logger.info("loadProcedure", "Attempting to load procedure", {
				role,
				procedure: procedureIDRef.current,
				resolved: procedurePath
			});

			const labProcedure = await ExecutorUtils.retryWithBackoff(
				async () => await VlabAPICtx.loadFileFromVLabScriptsCDN<Procedure>(`/procedures/${procedurePath}/user.json`),
				1000,
				3
			).promise();
			if (
				Array.isArray(labProcedure.availableLanguages) &&
				labProcedure.availableLanguages.length > 0 &&
				!labProcedure.availableLanguages.includes(LanguageCtx.language as Language)
			) {
				// Filter out any invalid languages and get the first valid one
				const validLanguage = labProcedure.availableLanguages
					.map((lang) => LanguageUtils.validateLanguage(lang as Language))
					.find((lang) => lang !== false) as Language | undefined;

				if (validLanguage) {
					logger.info("loadProcedure", `Switching to: ${validLanguage}`, {
						validLanguage,
						availableLanguages: labProcedure.availableLanguages,
						currentLanguage: LanguageCtx.language
					});
					LanguageCtx.changeLanguage(validLanguage as Language);
				} else {
					logger.info("loadProcedure", "No valid language found in availableLanguages. Keeping current language.", {
						availableLanguages: labProcedure.availableLanguages,
						currentLanguage: LanguageCtx.language
					});
				}
			}
			setProcedure(labProcedure);
			logger.info("loadProcedure", "Loaded procedure.", { role, procedure: procedureIDRef.current });
		} catch (e) {
			logger.error("loadProcedure", `Caught Error: ${e.message}`, e, { role, procedure: procedureIDRef.current });
			setInitializeError(e);
			await Modal.alert("An internal error occurred. This procedure could not be loaded.");
		}
	};

	const initializeTestSession = async (newProcedureID: string) => {
		try {
			if (initializingRef.current) return;
			AuthCtx.logout();
			setInitializeError(undefined);
			setInitializing(true);
			logger.info("initializeTestSession", "Starting test session...", { procedureID: newProcedureID });

			const response = await VlabAPICtx.call<TrainingSessionStartRequest, TrainingSessionStartResponse>(
				"POST",
				"/v1/training/session/$start",
				{
					data: {
						procedureID: newProcedureID,
						onFailureRedirectUri: "https://emed.com"
					}
				}
			);

			logger.debug("initializeTestSession", "Test Session start responded.", {
				procedureID: newProcedureID,
				response
			});

			const searchParams = new URLSearchParams();
			searchParams.append("token", response.token.token.value);
			Router.push(`/start/?${searchParams.toString()}`);

			setInitializing(false);
		} catch (e) {
			const error = e as HttpClientError;
			logger.error("initializeTestSession", `Caught Error: ${e.message}`, e, { data: error?.response?.data });
			if (EnvCtx.get(Config.FLAG_IS_LOCAL_DEVELOPMENT, { optional: true })) {
				const message = error?.response?.data?.message || error?.message || "Unknown";
				await Modal.alert(
					`There was a problem starting this session. Are VLAB API and your local mock resources running (  npm run watch )? Error: ${message}`
				);
			} else {
				await Modal.alert("There was a problem starting this session. Please try again.");
			}
			setInitializing(false);
			setInitializeError(e as any);
		}
	};

	/**
	 * Do work to setup the session
	 */
	const initialize = async (role: VLabRole) => {
		try {
			logger.debug("Session State Initialize called.", { sessionID: AuthCtx.sessionID });
			setInitializing(true);
			setInitializeError(undefined);
			setHeartbeatError(undefined);
			setSessionValid(true);
			setSessionID(AuthCtx.sessionID);
			const meta = await ExecutorUtils.retryWithBackoff(
				async () => await SessionData.getSessionMetadata(),
				1000,
				3
			).promise();
			setSessionMetadata(meta);
			logger.debug("Initialize: SessionMeta received", { meta: meta.record.data });

			const procedureValue = meta?.record?.data?.procedure?.identifier?.value;
			if (!procedureValue) {
				logger.warn("Initialize: No procedure value found in session metadata", { meta });
				throw new Error("No procedure found in session metadata");
			} else {
				/* const patient = meta.record.data.contained?.find(
					(c) => c.id === meta.record.data.patient.reference.replace(/^#+/, "")
				);
				;
				setPatient(patient);
				 */
				setProcedureID(procedureValue);
				switch (role) {
					case VLabRole.PATIENT: {
						setProcedureLocation(`${procedureValue}/user`);
						startupPatient();
						break;
					}
					case VLabRole.PROCTOR: {
						setProcedureLocation(`${procedureValue}/proctor`);
						startupProctor();
						break;
					}
					default: {
						throw new Error("Unhandled role");
					}
				}
			}
		} catch (error) {
			logger.error(`Caught Error in Session Initialize: ${error.message}`, {
				error,
				session_id: AuthCtx.sessionID
			});
			setInitializing(false);
			setInitializeError(error as any);
			await Modal.alert("There was a problem starting this session. Please try again.");
		}
	};

	const startupPatient = async () => {
		await loadProcedure(VLabRole.PATIENT);

		// STEP 1: Start with the initial query params we captured on mount
		let allParams: Record<string, string> = { ...(initialQueryParamsRef.current || {}) };

		// STEP 2: Also check current window.location.search in case we missed anything
		if (typeof window !== "undefined" && window.location.search) {
			const urlParams = new URLSearchParams(window.location.search);
			urlParams.forEach((value, key) => {
				// Exclude token - it should be consumed during auth
				if (key !== "token" && !allParams[key]) {
					allParams[key] = value;
				}
			});
		}

		// STEP 3: Merge with Router.query as additional fallback
		const routerParams = { ...Router.query };
		delete routerParams.token; // Remove token if it exists

		// Merge: prioritize captured initial params, then window.location, then Router.query
		const existingQuery = { ...routerParams, ...allParams };

		// STEP 4: Add our required params (overwrite pid/sid with correct values)
		const finalQuery = {
			...existingQuery,
			pid: procedureIDRef.current,
			sid: sessionIDRef.current
		};
		setInitializing(false);

		// STEP 5: Build the URL with query string
		const queryString = new URLSearchParams(finalQuery as any).toString();
		const targetUrl = `/procedure/?${queryString}`;

		// STEP 6: Navigate if needed
		if (Router.asPath !== targetUrl) {
			await Router.replace(targetUrl, undefined, { shallow: true });
		}
	};

	const startupProctor = async () => {
		await loadProcedure(VLabRole.PROCTOR);
		// Preserve all existing query parameters
		const existingQuery = { ...Router.query };
		setInitializing(false);

		if (Router.route !== "/proctor/") {
			Router.replace(
				{
					pathname: "/proctor/",
					query: existingQuery
				},
				undefined,
				{ shallow: true }
			);
		}
	};

	const value: SessionStateContext = {
		initialize,
		initializeError,
		sessionID,
		procedureID,
		procedure,
		procedureLocation,
		initializing,
		sessionMetadata,
		heartbeatError,
		sessionValid
	};
	return <SessionStateContext.Provider value={value}>{props.children}</SessionStateContext.Provider>;
};
